import { useState, useEffect } from 'react';
import { countries, defaultCountry } from '../../data/countries.js';
import CountryFlag from './CountryFlag.jsx';
import './CountrySelector.css';

function CountrySelector({ value, onChange, errors = {}, disabled = false, required = true }) {
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialiser avec la valeur existante ou le pays par défaut
  useEffect(() => {
    if (value) {
      const country = countries.find(c => c.code === value || c.name === value);
      if (country) {
        // Syncing local UI state from external `value` prop is the standard
        // pattern for this controlled-but-with-internal-derivations widget.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedCountry(country);
      }
    }
  }, [value]);

  // Notifier le parent quand le pays change
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchTerm('');
    if (onChange) {
      onChange({
        country: country.name,
        country_code: country.code,
        phone_code: country.phoneCode, // Pour mettre à jour le téléphone automatiquement
      });
    }
  };

  // Filtrer les pays selon la recherche
  const filteredCountries = searchTerm
    ? countries.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phoneCode.includes(searchTerm)
      )
    : countries;

  return (
    <div className="country-selector-wrapper">
      <label htmlFor="country-selector" className="sr-only">
        Pays {required && <span className="required">*</span>}
      </label>
      <div className="country-selector">
        <button
          type="button"
          id="country-selector"
          className={`country-selector-button ${isOpen ? 'open' : ''} ${errors.country ? 'error' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-label={`Pays sélectionné: ${selectedCountry.name}`}
        >
          <CountryFlag country={selectedCountry} aria-hidden="true" />
          <span className="country-name">{selectedCountry.name}</span>
          <span className="dropdown-arrow">▼</span>
        </button>
        
        {isOpen && (
          <>
            <div className="dropdown-overlay" onClick={() => setIsOpen(false)}></div>
            <div className="country-dropdown">
              <div className="country-search">
                <input
                  type="text"
                  placeholder="Rechercher un pays..."
                  className="country-search-input"
                  value={searchTerm}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="country-list">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <div
                      key={country.code}
                      className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                      onClick={() => handleCountrySelect(country)}
                    >
                      <CountryFlag country={country} aria-hidden="true" />
                      <span className="country-name">{country.name}</span>
                      <span className="country-phone-code">{country.phoneCode}</span>
                    </div>
                  ))
                ) : (
                  <div className="country-no-results">Aucun pays trouvé</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {errors.country && (
        <div className="field-error">{errors.country}</div>
      )}
    </div>
  );
}

export default CountrySelector;
